import { css } from '@emotion/core'
import React, { Children, type ReactNode } from 'react'
import { Arrow } from '@fs/zion-icon'
import { Cell, Row } from '@fs/zion-ui'
import { type CompanionColumn, useMergeGridContext } from './providers/MergeGridProvider'
import useMergeMarginX from './layout/useMergeMarginX'

const gridCss = css`
  display: grid;

  & > :first-of-type {
    grid-column: span 2;
  }
`

const hiddenArrowCss = css`
  /** Spacing for the arrow insert from ArrowDecorator. */
  visibility: hidden;
`

const arrowCss = css`
  /** Move Arrow into spacing for arrow in MergeGrid. */
  align-self: center;
  position: relative;
  width: 0;
`

// Clips the switched grid to two columns; clip, unlike hidden, keeps the page the scroller for sticky content
const columnWindowCss = css`
  overflow-x: clip;
`

const cellStyle = { minWidth: 0 }
const onTopStyle = { position: 'relative', zIndex: 1 } as const

const slideCss = css`
  transition: margin-inline-start 0.3s ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

type MergeGridProps = {
  children: ReactNode
  arrowDiv?: ReactNode
}

/** Grid for 2 children, an arrow or space, and the last child where all the children have the same width and height.
 * The arrow or space, and last child are removed in mobile sizes.
 */
export default function MergeGrid({ children, arrowDiv }: MergeGridProps): JSX.Element {
  const { companionColumn, showThirdColumn } = useMergeGridContext()

  if (companionColumn) {
    return <SwitchedMergeGrid companionColumn={companionColumn}>{children}</SwitchedMergeGrid>
  }

  const decidingIndex = 1
  return (
    <div
      css={gridCss}
      style={{
        gridTemplateColumns: showThirdColumn ? '1fr 1fr auto 1fr' : '1fr 1fr',
        gap: '0px',
      }}
    >
      {Children.map(children, (child, childIndex) => {
        if (childIndex >= decidingIndex) return null
        // Min width is required to make sure there is no overflow from Ellipsis children.
        return <div style={{ minWidth: 0 }}>{child}</div>
      })}

      {showThirdColumn && (
        <>
          {arrowDiv || (
            <div css={hiddenArrowCss}>
              <Arrow />
            </div>
          )}

          {Children.map(children, (child, childIndex) => {
            // Min width is required to make sure there is no overflow from Ellipsis children.
            if (childIndex === decidingIndex) return <div style={{ minWidth: 0 }}>{child}</div>
            return null
          })}
        </>
      )}
    </div>
  )
}

type SwitchedMergeGridProps = {
  children: ReactNode
  companionColumn: CompanionColumn
}

/**
 * All three columns at half the width each, in a window two columns wide that slides to show the Main person beside
 * the duplicate or the resultant. Every MergeGrid on the page slides together, so their rows stay aligned. No arrow:
 * there is no room for one.
 */
function SwitchedMergeGrid({ children, companionColumn }: SwitchedMergeGridProps): JSX.Element {
  const marginX = useMergeMarginX()
  let marginInlineStart = '0px'
  // The duplicate's edge-to-edge card bleeds into the page gutter for its padding; the window's padding takes in the
  // gutter, since clip cuts at the padding edge, without moving the grid
  let windowGutter = marginX
  if (companionColumn === 'resultant') {
    // Half of the window: the duplicate column slides out, and the Main person's column needs no gutter
    marginInlineStart = '-50%'
    windowGutter = 0
  }

  // The end gutter shows the Main person's edge-to-edge card bleed beside the duplicate, so its text keeps the same
  // padding as the duplicate's. The card has to paint over the start of the hidden Final result for that.
  const cardOnTop = companionColumn === 'duplicate'

  return (
    <div
      css={columnWindowCss}
      style={{
        marginInlineEnd: -marginX,
        marginInlineStart: -windowGutter,
        paddingInlineEnd: marginX,
        paddingInlineStart: windowGutter,
      }}
    >
      <div
        css={[gridCss, slideCss]}
        style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginInlineStart, width: '150%' }}
        data-companion-column={companionColumn}
        data-testid="switched-merge-grid"
      >
        {Children.map(children, (child, childIndex) => (
          // Min width is required to make sure there is no overflow from Ellipsis children.
          <div style={childIndex === 0 && cardOnTop ? { ...cellStyle, ...onTopStyle } : cellStyle}>{child}</div>
        ))}
      </div>
    </div>
  )
}

type ArrowDecoratorProps = {
  children: ReactNode
}

/** Adds Arrow to end of children; to be used with MergeGrid to align the arrow vertically in the middle of children. */
export function ArrowDecorator({ children }: ArrowDecoratorProps): JSX.Element {
  const { showThirdColumn } = useMergeGridContext()

  return (
    <Row gutters="none" alignItemsY="middle">
      <Cell growX>{children}</Cell>
      {showThirdColumn && (
        <div css={arrowCss}>
          <Arrow />
        </div>
      )}
    </Row>
  )
}
